export default () => ({
  canScrollLeft: false,
  canScrollRight: false,
  _resizeTimeout: null,

  init() {
    this.$nextTick(() => {
      this.updateArrows()

      this._scrollHandler = this.maybeAlignToActive.bind(this)
      this._hashHandler = () => {
        this.scrollToHash(window.location.hash)
        this.maybeAlignToActive()
      }
      this._resizeHandlerFn = () => {
        clearTimeout(this._resizeTimeout)
        this._resizeTimeout = setTimeout(() => {
          this.updateArrows()
          this.maybeAlignToActive()
        }, 100)
      }

      window.addEventListener('scroll', this._scrollHandler, { passive: true })
      window.addEventListener('hashchange', this._hashHandler)
      window.addEventListener('resize', this._resizeHandlerFn)

      // Expose a helper for global callers (e.g. calculateScrollAnchor)
      window.scrollSubnavToActive = () => this.maybeAlignToActive()

      // Correct native fragment scrolling after the whole page has rendered. The resource
      // form lives in <main>; allowing the browser to scroll every ancestor also moves the
      // viewport by the header height and leaves a blank strip below the h-screen app shell.
      setTimeout(() => {
        this.scrollToHash(window.location.hash)
        this.maybeAlignToActive()
      }, 0)
    })
  },

  destroy() {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler, { passive: true })
    }
    if (this._hashHandler) {
      window.removeEventListener('hashchange', this._hashHandler)
    }
    if (this._resizeHandlerFn) {
      window.removeEventListener('resize', this._resizeHandlerFn)
    }
    clearTimeout(this._resizeTimeout)
    delete window.scrollSubnavToActive
  },

  onScroll() {
    this.updateArrows()
  },

  updateArrows() {
    const el = this.$refs.scroller
    if (!el) {
      return
    }
    this.canScrollLeft = el.scrollLeft > 0
    this.canScrollRight = (el.scrollLeft + el.clientWidth) < (el.scrollWidth - 1)
  },

  scrollByAmount(amount) {
    const el = this.$refs.scroller
    if (!el) {
      return
    }
    const dir = Math.sign(amount) || 1
    const remaining = dir > 0
      ? (el.scrollWidth - el.clientWidth - el.scrollLeft)
      : el.scrollLeft
    const delta = Math.min(400, Math.max(0, remaining))
    el.scrollTo({ left: el.scrollLeft + dir * delta, behavior: 'smooth' })
    this.$nextTick(() => this.updateArrows())
  },

  scrollLeft() { this.scrollByAmount(-400) },
  scrollRight() { this.scrollByAmount(400) },

  navigateToAnchor(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    const hash = event.currentTarget?.getAttribute('href')
    if (!this.scrollToHash(hash)) {
      return
    }

    event.preventDefault()

    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash)
    }
  },

  scrollToHash(hash) {
    if (!hash?.startsWith('#') || hash.length === 1) {
      return false
    }

    let id
    try {
      id = decodeURIComponent(hash.substring(1))
    } catch {
      return false
    }

    const target = document.getElementById(id)
    const main = document.querySelector('main')
    if (!target || !main) {
      return false
    }

    const targetRect = target.getBoundingClientRect()
    const mainRect = main.getBoundingClientRect()
    const scrollMarginTop = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0
    const top = main.scrollTop + targetRect.top - mainRect.top - scrollMarginTop

    window.scrollTo({ top: 0, left: 0 })
    main.scrollTo({ top: Math.max(0, top) })
    window.calculateScrollAnchor?.()

    return true
  },

  isElementFullyVisible(target, container) {
    const t = target.getBoundingClientRect()
    const c = container.getBoundingClientRect()
    return t.left >= c.left && t.right <= c.right
  },

  maybeAlignToActive() {
    const el = this.$refs.scroller
    if (!el) {
      return
    }
    // Active anchor is the one marked with the 'active' class by calculateScrollAnchor
    const active = el.querySelector('a.active')
    if (!active) {
      this.updateArrows()
      return
    }
    if (!this.isElementFullyVisible(active, el)) {
      const target = active.getBoundingClientRect()
      const container = el.getBoundingClientRect()
      const left = el.scrollLeft + target.left - container.left - ((el.clientWidth - target.width) / 2)

      el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
    }
    this.updateArrows()
  }
})
